import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay, applyFilterOverlayImage} from '@renderer/utils/lutUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@renderer/hooks/useFeatureFlag'
import { useFullscreen } from '@renderer/utils'
import i18n from '@renderer/i18n'
import Button from '@renderer/components/bahela/Button'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { FaExchangeAlt } from 'react-icons/fa'
import { useKotakCropStore, kotakSides } from '@renderer/stores/kotakCropStore'
import {
  centeredSensorRect,
  refitRectToRatio,
  sensorRectToDisplayRect,
  displayRectToSensorRect,
  displayAspect,
  normalizeRotation,
  type CropRect,
  type PreviewRotation
} from '@renderer/utils/kotakCrop'
import CropEditor from './CropEditor'
import BahelaCapturePage from '../bahela/Capture'

type CameraKey = 'primary' | 'secondary'

const RATIO_PRESETS: { label: string; value: number }[] = [
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 }
]

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms))

/**
 * Bahelakotak — dual-camera wardrobe booth capture page.
 *
 * Two cameras stream CONCURRENTLY, one per wardrobe "door" pane; every countdown fires
 * both cameras, so each capture round yields TWO photos (left box + right box). The
 * secondary camera is best-effort: if it is not connected (or dies mid-session) the
 * page degrades to a single centered pane and one photo per round — a session must
 * never be lost because one body hiccuped.
 *
 * CUSTOM-BOX CROP (kotak). The point of a kotak booth is that each live feed is cropped to
 * the exact rectangle of a physical box window so the on-screen image lines up seamlessly
 * with the box. Because the two cameras never sit identically, the crop is an arbitrary,
 * per-camera rectangle — not a centered square. We treat the UNLOCKED (unfullscreen) app as
 * "admin mode": the operator sees the FULL uncropped sensor and drags a ratio-locked box over
 * each pane; the box is persisted per project (localStorage) in unrotated sensor space. When
 * LOCKED (fullscreen, end-user mode) each pane renders just the cropped region, tidy and
 * centered, and every capture — in either mode — saves exactly that region.
 *
 * Both cameras are connected at boot by View.handleCameraConnect (gated on
 * customPath === 'bahelakotak'); LEFT = settingsStore.selectedCamera ('primary'),
 * RIGHT = selectedSecondaryCamera ('secondary').
 */
const KotakDualCapture: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, userPreferences, id, uploadSessionState, logActivity } =
    useSessionStateStore()
  const { config: globalConfig, getFilter, filters, getFrame } = useConfigStore()
  const config = globalConfig?.pages.capture
  const { cameraSettings, secondaryCameraSettings, downloadLocal } = useSettingsStore()
  const { fetchSubscriptionAndCredits } = useCreditsStore()
  const { isFullscreen } = useFullscreen()

  // Unlocked window == operator/admin. Locked (fullscreen) == live guest flow.
  const editing = !isFullscreen

  // Dual mode requires the dual_camera feature flag (account-targeted kill switch) AND
  // a live secondary instance; losing either degrades to single-pane mid-session.
  const dualCameraFlag = useFeatureFlag('dual_camera')
  const secondaryWebcam = useCameraStore((s) => s.secondaryWebcam)
  const secondaryCamera = useCameraStore((s) => s.secondaryCamera)
  const secondaryConnected = Boolean(secondaryWebcam || secondaryCamera)
  const dualActive = dualCameraFlag && secondaryConnected
  // Operator diagnostic: the secondary IS live but the dual_camera flag suppresses it —
  // without this hint the page silently runs single-pane and looks like a broken config.
  const dualSuppressedByFlag = !dualCameraFlag && secondaryConnected

  // ── Crop store (per project) ───────────────────────────────────────────────────────────────
  const projectKey = projectId || 'default'
  // Select stable references (not a fresh object) so zustand's snapshot stays cached.
  const cropRatio = useKotakCropStore((s) => s.getProjectCrop(projectKey).ratio)
  const savedPrimary = useKotakCropStore((s) => s.getProjectCrop(projectKey).primary)
  const savedSecondary = useKotakCropStore((s) => s.getProjectCrop(projectKey).secondary)
  const savedRects: Record<CameraKey, CropRect | undefined> = {
    primary: savedPrimary,
    secondary: savedSecondary
  }
  const setRatio = useKotakCropStore((s) => s.setRatio)
  const setRect = useKotakCropStore((s) => s.setRect)
  const resetProject = useKotakCropStore((s) => s.resetProject)
  // Operator L/R swap (app-layer role map): which physical camera sits in the left vs right box.
  const swapped = useKotakCropStore((s) => s.getProjectCrop(projectKey).swapped)
  const toggleSwapped = useKotakCropStore((s) => s.toggleSwapped)
  const { leftKey, rightKey } = kotakSides(swapped)

  // Per-camera rotation/mirror (the crop editor maps display⇄sensor through these).
  const rot: Record<CameraKey, PreviewRotation> = {
    primary: normalizeRotation(cameraSettings?.previewRotation),
    secondary: normalizeRotation(secondaryCameraSettings?.previewRotation)
  }
  const mir: Record<CameraKey, boolean> = {
    primary: cameraSettings?.mirror ?? true,
    secondary: secondaryCameraSettings?.mirror ?? true
  }

  // Raw sensor dimensions per camera, needed only in admin mode to size the editor and derive
  // the centered default box. Locked mode never needs them (a saved rect is already normalized;
  // with none, Preview falls back to a centered ratio crop identical to the legacy behavior).
  const [sensorDims, setSensorDims] = useState<Partial<Record<CameraKey, { w: number; h: number }>>>(
    {}
  )
  const [testShots, setTestShots] = useState<{ left?: string; right?: string }>({})
  const [testing, setTesting] = useState(false)

  const uniquePhotoTypes = new Set(
    getFrame(userPreferences.frameId || '')
      ?.drawParams.images.map((image) => image.type)
      .filter((type) => type.startsWith('photo-'))
  )
  const uniquePhotoCount = uniquePhotoTypes.size
  const defaultCaptureCount = 4
  // Total PHOTOS the strip needs (not rounds). Dual mode fills 2 per round.
  const totalPhotos = Math.max(uniquePhotoCount, config?.settings.captureCount || defaultCaptureCount)

  const initialCaptureCountdown = config?.settings.captureCountdown || 5
  const [captureCountdown, setCaptureCountdown] = useState<number>(initialCaptureCountdown)
  const [preparing, setPreparing] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [prepCountdown, setPrepCountdown] = useState<string>('00:00')
  const [lastCaptured, setLastCaptured] = useState<{ left?: string; right?: string }>({})
  const [isCaptureError, setIsCaptureError] = useState(false)
  // "Stay still" feedback during a physical-shutter round. Unlike the classic pages,
  // the dual DSLR path transfers TWO full-size files sequentially over USB (PTP is
  // single-outstanding per device) with nothing shown — guests had no idea the booth
  // was working. This overlay covers exactly that window. (The countdown is NOT
  // decoupled here on purpose: the second camera's in-flight download must not overlap
  // the next round, or the two would contend on the secondary's PTP channel.)
  const [isCapturingWithShutter, setIsCapturingWithShutter] = useState(false)

  const prepTime = userPreferences.retakeCount > 0 ? 1 : config?.settings.prepTimeout || 5
  const captureTime = config?.settings.captureCountdown || 5

  const prepCountdownHook = useCountdown(prepTime)
  const captureCountdownHook = useCountdown(captureTime, { audioCue: true })
  const stopCaptureRef = useRef<boolean>(false)
  const startedRef = useRef<boolean>(false)

  const remainingPhotosRef = useRef<number>(
    userPreferences.retakeCount > 0 ? userPreferences.retakeCount : totalPhotos
  )
  const selectedFilter = getFilter(userPreferences.filterId || filters[0]._id)

  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (selectedFilter?.adjustments) {
      filterUtils.applyAdjustments(ctx, selectedFilter.adjustments)
      applyFilterOverlayImage(ctx, selectedFilter?.overlay)
    }
  }

  /** Fresh crop rects straight from the store (avoids stale closures at capture time). */
  const liveCropRects = useCallback((): { primary?: CropRect; secondary?: CropRect } => {
    const c = useKotakCropStore.getState().getProjectCrop(projectKey)
    return { primary: c.primary, secondary: c.secondary }
  }, [projectKey])

  const useShutterPrimary = !cameraSettings.useShutterCapture

  // ── Live-mode recording (strip_live / strip_story) ───────────────────────────────────────
  // Same mechanism as the default /view flow: a canvas-record Recorder bound to each pane's
  // rotation-baked "recording canvas" (Preview.onRecordingCanvasReady) is stepped through the
  // countdown, producing one short MP4 clip per capture. Dual mode records BOTH panes so the
  // left/right photos each carry their own clip; Select then assembles the live-mode video.
  const useRecording = Boolean(userPreferences.useRecording)
  const recordingType = userPreferences.recordingType
  const shouldRecord =
    useRecording && (recordingType === 'strip_live' || recordingType === 'strip_story')
  const RECORD_FPS = 30

  const primaryRecCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const secondaryRecCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const [primaryRecDims, setPrimaryRecDims] = useState<{ w: number; h: number } | null>(null)
  const [secondaryRecDims, setSecondaryRecDims] = useState<{ w: number; h: number } | null>(null)
  const primaryRecorderRef = useRef<Recorder | null>(null)
  const secondaryRecorderRef = useRef<Recorder | null>(null)

  const onPrimaryRecCanvasReady = useCallback(
    (el: HTMLCanvasElement, _ctx: CanvasRenderingContext2D, dim: { width: number; height: number }) => {
      primaryRecCanvasRef.current = el
      setPrimaryRecDims({ w: dim.width, h: dim.height })
    },
    []
  )
  const onSecondaryRecCanvasReady = useCallback(
    (el: HTMLCanvasElement, _ctx: CanvasRenderingContext2D, dim: { width: number; height: number }) => {
      secondaryRecCanvasRef.current = el
      setSecondaryRecDims({ w: dim.width, h: dim.height })
    },
    []
  )

  // Wire a screen pane (which renders `cameraKey`) to that PHYSICAL camera's recorder canvas,
  // so recorders stay physical-keyed regardless of the L/R swap.
  const onRecCanvasReadyFor = (
    key: CameraKey
  ): ((el: HTMLCanvasElement, ctx: CanvasRenderingContext2D, dim: { width: number; height: number }) => void) =>
    key === 'primary' ? onPrimaryRecCanvasReady : onSecondaryRecCanvasReady

  const evenDim = (w: number, h: number): { w: number; h: number } => ({
    w: Math.max(2, Math.floor(w / 2) * 2),
    h: Math.max(2, Math.floor(h / 2) * 2)
  })

  const makeRecorder = (
    canvasEl: HTMLCanvasElement,
    dims: { w: number; h: number },
    name: string
  ): Recorder | null => {
    const ctx = canvasEl.getContext('2d')
    if (!ctx) return null
    const d = evenDim(canvasEl.width || dims.w, canvasEl.height || dims.h)
    try {
      return new Recorder(ctx as unknown as RenderingContext, {
        name,
        encoderOptions: {
          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
          width: d.w,
          height: d.h,
          bitrate: 2000000
        },
        duration: Infinity,
        download: downloadLocal,
        frameRate: RECORD_FPS
      })
    } catch (error) {
      console.error(`[KOTAK] Failed to create recorder ${name}:`, error)
      return null
    }
  }

  // Create/refresh the primary recorder once its recording canvas reports dimensions.
  useEffect(() => {
    if (!shouldRecord) return
    const el = primaryRecCanvasRef.current
    if (!el || !primaryRecDims || primaryRecDims.w <= 0) return
    try {
      primaryRecorderRef.current?.stop()
    } catch {
      /* ignore */
    }
    primaryRecorderRef.current = makeRecorder(el, primaryRecDims, `dreambooth-${id}-kotak-L`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRecord, primaryRecDims, id, downloadLocal])

  // Secondary recorder — only when dual capture is actually active.
  useEffect(() => {
    if (!shouldRecord || !dualActive) return
    const el = secondaryRecCanvasRef.current
    if (!el || !secondaryRecDims || secondaryRecDims.w <= 0) return
    try {
      secondaryRecorderRef.current?.stop()
    } catch {
      /* ignore */
    }
    secondaryRecorderRef.current = makeRecorder(el, secondaryRecDims, `dreambooth-${id}-kotak-R`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRecord, dualActive, secondaryRecDims, id, downloadLocal])

  // Stop any live recorders on unmount so a mid-round encoder can't leak.
  useEffect(() => {
    return (): void => {
      try {
        primaryRecorderRef.current?.stop()
      } catch {
        /* ignore */
      }
      try {
        secondaryRecorderRef.current?.stop()
      } catch {
        /* ignore */
      }
    }
  }, [])

  /** Start the round's recorders (best-effort; a not-ready recorder simply records no clip). */
  const startRoundRecorders = async (): Promise<void> => {
    const startOne = async (ref: React.MutableRefObject<Recorder | null>): Promise<void> => {
      const r = ref.current
      if (!r || r.status === RecorderStatus.Recording) return
      try {
        await r.start()
      } catch (error) {
        console.error('[KOTAK] recorder start failed:', error)
      }
    }
    await Promise.all([
      startOne(primaryRecorderRef),
      dualActive ? startOne(secondaryRecorderRef) : Promise.resolve()
    ])
  }

  /** Drive both recorders frame-by-frame across the countdown window. */
  const stepRoundRecorders = async (): Promise<void> => {
    const totalFrames = Math.ceil(captureTime * RECORD_FPS)
    const start = performance.now()
    let frame = 0
    const anyRecording = (): boolean =>
      primaryRecorderRef.current?.status === RecorderStatus.Recording ||
      (dualActive && secondaryRecorderRef.current?.status === RecorderStatus.Recording)
    while (frame < totalFrames && !stopCaptureRef.current && anyRecording()) {
      const elapsed = performance.now() - start
      if (elapsed >= (frame * 1000) / RECORD_FPS) {
        const steps: Promise<unknown>[] = []
        if (primaryRecorderRef.current?.status === RecorderStatus.Recording) {
          steps.push(primaryRecorderRef.current.step())
        }
        if (dualActive && secondaryRecorderRef.current?.status === RecorderStatus.Recording) {
          steps.push(secondaryRecorderRef.current.step())
        }
        try {
          await Promise.all(steps)
        } catch (error) {
          console.error('[KOTAK] recorder step failed:', error)
        }
        frame++
      }
      await new Promise((r) => setTimeout(r, 1))
    }
  }

  /** Stop both recorders and return a blob URL per side (undefined when empty/failed). */
  const stopRoundRecorders = async (): Promise<{ primaryUrl?: string; secondaryUrl?: string }> => {
    const stopOne = async (
      ref: React.MutableRefObject<Recorder | null>
    ): Promise<string | undefined> => {
      const r = ref.current
      if (!r || r.status !== RecorderStatus.Recording) return undefined
      try {
        const chunks = await r.stop()
        if (!chunks) return undefined
        const blob = new Blob([chunks as ArrayBuffer], { type: 'video/mp4' })
        return blob.size > 0 ? URL.createObjectURL(blob) : undefined
      } catch (error) {
        console.error('[KOTAK] recorder stop failed:', error)
        return undefined
      }
    }
    const [primaryUrl, secondaryUrl] = await Promise.all([
      stopOne(primaryRecorderRef),
      dualActive ? stopOne(secondaryRecorderRef) : Promise.resolve(undefined)
    ])
    return { primaryUrl, secondaryUrl }
  }

  // ── Sensor dimensions (admin only) ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!editing) return
    let cancelled = false
    const fetchDims = async (key: CameraKey): Promise<void> => {
      for (let i = 0; i < 25 && !cancelled; i++) {
        try {
          const blob = await unifiedCameraUtils.capturePreview(key, {
            maxWidth: 1280,
            maxHeight: 720,
            quality: 0.5,
            rotation: 0,
            mirror: false
          })
          if (blob) {
            const bmp = await createImageBitmap(blob)
            const w = bmp.width
            const h = bmp.height
            bmp.close()
            if (!cancelled && w > 0 && h > 0) {
              setSensorDims((prev) => ({ ...prev, [key]: { w, h } }))
              return
            }
          }
        } catch {
          // camera warming up / busy — retry
        }
        await sleep(400)
      }
    }
    fetchDims('primary')
    if (dualActive) fetchDims('secondary')
    return (): void => {
      cancelled = true
    }
  }, [editing, dualActive])

  // ── Capture machinery ────────────────────────────────────────────────────────────────────
  // `physicalKey` is the actual camera the shot came from (for pairing its live clip); `side`
  // is the box the operator assigned it to via the L/R swap.
  type RoundShot = { id: string; photoUrl: string; side: 'L' | 'R'; physicalKey: CameraKey }

  /**
   * Fire the cameras for one round and return the captured shot(s). Does NOT persist to the
   * gallery — the caller pairs each shot with its live-mode clip first (L→primary, R→secondary).
   */
  const captureRoundImages = async (photosNeeded: number): Promise<RoundShot[]> => {
    flashEffect('.flash')
    setCapturing(false)
    setIsCaptureError(false)
    if (cameraSettings.useShutterCapture && useCameraStore.getState().deviceType === 'camera') {
      setIsCapturingWithShutter(true)
    }

    await new Promise((resolve) => setTimeout(resolve, 100))

    const useBoth = dualActive && photosNeeded >= 2
    const captureId = id + '-' + Date.now()
    const captured: RoundShot[] = []
    const cropRects = liveCropRects()

    try {
      if (useBoth) {
        // Crop each camera to its saved box (unrotated sensor space). A side with no saved
        // box falls back to a centered `cropRatio` crop (== legacy square when ratio is 1).
        const result = await unifiedCameraUtils.captureBothImages(
          applyFilter,
          cropRatio,
          useShutterPrimary,
          undefined,
          cropRects
        )
        // Assign each physical camera to the box the operator picked via the L/R swap.
        const leftImg = result[leftKey]
        const rightImg = result[rightKey]
        if (leftImg) {
          const url = URL.createObjectURL(leftImg)
          captured.push({ id: `${captureId}-L`, photoUrl: url, side: 'L', physicalKey: leftKey })
          setLastCaptured((prev) => ({ ...prev, left: url }))
        }
        if (rightImg) {
          const url = URL.createObjectURL(rightImg)
          captured.push({ id: `${captureId}-R`, photoUrl: url, side: 'R', physicalKey: rightKey })
          setLastCaptured((prev) => ({ ...prev, right: url }))
        }
      } else {
        const imageData = await unifiedCameraUtils.captureImage(
          'primary',
          applyFilter,
          cropRatio,
          useShutterPrimary,
          undefined,
          cropRects.primary
        )
        if (imageData) {
          const url = URL.createObjectURL(imageData)
          captured.push({ id: `${captureId}-L`, photoUrl: url, side: 'L', physicalKey: 'primary' })
          setLastCaptured((prev) => ({ ...prev, left: url }))
        }
      }
    } catch (error) {
      console.error('[KOTAK] Capture round failed:', error)
    } finally {
      setIsCapturingWithShutter(false)
    }

    return captured
  }

  const runCaptureLoop = async (): Promise<void> => {
    while (remainingPhotosRef.current > 0 && !stopCaptureRef.current) {
      const photosNeeded = remainingPhotosRef.current

      // Start this round's live-mode recorders (best-effort) before the countdown runs.
      if (shouldRecord) await startRoundRecorders()

      setCapturing(true)
      // Autofocus now, while the countdown runs, so the shutter is not waiting on it
      // (camera_prefocus). Named explicitly here: this booth shoots BOTH bodies in the
      // same round, so the session's single selectedCamera is not the whole story.
      unifiedCameraUtils.prefocus('primary')
      if (dualActive) unifiedCameraUtils.prefocus('secondary')
      captureCountdownHook.start()

      // The shot is taken when the countdown completes; step the recorders alongside it.
      let roundShots: RoundShot[] = []
      const captureCompletion = new Promise<void>((resolve) => {
        captureCountdownHook.setOnComplete(async (): Promise<void> => {
          roundShots = await captureRoundImages(photosNeeded)
          resolve()
        })
      })
      if (shouldRecord) await stepRoundRecorders()
      await captureCompletion

      // Finalize the clips and pair each with its photo (L→primary pane, R→secondary pane).
      let primaryUrl: string | undefined
      let secondaryUrl: string | undefined
      if (shouldRecord) {
        const urls = await stopRoundRecorders()
        primaryUrl = urls.primaryUrl
        secondaryUrl = urls.secondaryUrl
      }

      if (roundShots.length === 0) {
        logActivity('Kotak capture round failed: no image data from either camera', {
          dualActive,
          level: 'error'
        })
        setIsCaptureError(true)
        // A fully-failed round retries after a beat instead of burning a slot;
        // stopCaptureRef still exits cleanly if the operator quits.
        await new Promise((r) => setTimeout(r, 1500))
      } else {
        const items = roundShots.map((shot) => ({
          id: shot.id,
          photoUrl: shot.photoUrl,
          // Clip follows the PHYSICAL camera the shot came from (recorders are physical-keyed).
          recordingUrl: (shot.physicalKey === 'secondary' ? secondaryUrl : primaryUrl) || '',
          timestamp: new Date()
        }))
        const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
        setSessionState({
          gallery: { capturedItems: [...prevItems, ...items] }
        })
        logActivity('Kotak photos captured', {
          count: items.length,
          dual: dualActive && photosNeeded >= 2,
          hasRecording: Boolean(primaryUrl || secondaryUrl),
          remainingPhotos: remainingPhotosRef.current - items.length
        })
        remainingPhotosRef.current = Math.max(0, remainingPhotosRef.current - items.length)
      }

      if (remainingPhotosRef.current > 0) {
        captureCountdownHook.setRemainingSeconds(captureTime)
      }
    }

    uploadSessionState()
    if (stopCaptureRef.current) return

    const maxRetakeSession = globalConfig?.pages.retake?.settings?.maxRetakeSession
    const retakeSessionCount = userPreferences.retakeSessionsCompleted || 0
    const canRetake =
      maxRetakeSession === undefined ||
      maxRetakeSession === null ||
      retakeSessionCount < maxRetakeSession

    if (canRetake) {
      navigate(`/${projectId}/retake`)
    } else {
      navigate(`/${projectId}/frame`)
    }
  }

  const startCaptureSession = async (): Promise<void> => {
    stopCaptureRef.current = false

    const isFirstCapture = userPreferences.retakeCount === 0
    if (isFirstCapture) {
      // Refresh auth/credits without blocking session start (offline sessions queue).
      if (navigator.onLine) {
        try {
          await Promise.race([
            fetchSubscriptionAndCredits(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
          ])
        } catch (error) {
          console.warn('[KOTAK] Failed to fetch subscription/credits, proceeding anyway:', error)
        }
      } else {
        fetchSubscriptionAndCredits().catch(() => undefined)
      }
      setTimeout(() => {
        uploadSessionState()
      }, 2000)
    }

    logActivity('Kotak capture session started', {
      dualActive,
      totalPhotos,
      isRetake: userPreferences.retakeCount > 0
    })

    prepCountdownHook.stop()
    setPreparing(false)
    setCapturing(true)
    runCaptureLoop()
  }

  // Set up countdown hook callbacks once.
  useEffect(() => {
    uploadSessionState()

    prepCountdownHook.setOnTick(() => {
      setPrepCountdown(prepCountdownHook.getFormattedRemainingSeconds())
    })
    prepCountdownHook.setOnComplete(() => {
      logActivity('Prep countdown timed out', { type: 'prep', duration: prepTime })
      startCaptureSession()
    })

    captureCountdownHook.setOnTick(() => {
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
      setCapturing(true)
    })

    return (): void => {
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      stopCaptureRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Drive the live guest flow only while LOCKED. Entering fullscreen starts prep (or capture);
  // leaving it (operator unlocks to align boxes) halts everything and reveals the editor.
  useEffect(() => {
    if (isFullscreen) {
      if (startedRef.current) return
      startedRef.current = true
      const isRetake = userPreferences.retakeCount > 0
      setPreparing(userPreferences.retakeCount === 0)
      const prepTimeout = config?.settings.prepTimeout
      if ((prepTimeout === null || prepTimeout === 0 || prepTimeout === undefined) && !isRetake) {
        startCaptureSession()
      } else {
        prepCountdownHook.start()
      }
    } else {
      // Unlocked → stop the guest flow; the operator is aligning boxes.
      startedRef.current = false
      stopCaptureRef.current = true
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      setCapturing(false)
      setPreparing(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen])

  // ── Admin: ratio + editor handlers ───────────────────────────────────────────────────────
  const applyRatio = (value: number): void => {
    if (!(value > 0)) return
    setRatio(projectKey, value)
    // Keep each existing box roughly where it was, re-fitted to the new aspect.
    ;(['primary', 'secondary'] as CameraKey[]).forEach((key) => {
      const dims = sensorDims[key]
      const existing = savedRects[key]
      if (existing && dims) {
        setRect(projectKey, key, refitRectToRatio(existing, value, dims.w, dims.h, rot[key]))
      }
    })
  }

  /** Sensor rect actually in effect for a pane (saved, else centered default). */
  const effectiveSensorRect = (key: CameraKey): CropRect | undefined => {
    if (savedRects[key]) return savedRects[key]
    const dims = sensorDims[key]
    if (!dims) return undefined
    return centeredSensorRect(cropRatio, dims.w, dims.h, rot[key])
  }

  const onEditorChange = (key: CameraKey, displayRect: CropRect): void => {
    setRect(projectKey, key, displayRectToSensorRect(displayRect, rot[key], mir[key]))
  }

  const runTestCapture = async (): Promise<void> => {
    if (testing) return
    setTesting(true)
    try {
      const cropRects = liveCropRects()
      if (dualActive) {
        const result = await unifiedCameraUtils.captureBothImages(
          applyFilter,
          cropRatio,
          useShutterPrimary,
          undefined,
          cropRects
        )
        setTestShots({
          left: result[leftKey] ? URL.createObjectURL(result[leftKey] as Blob) : undefined,
          right: result[rightKey] ? URL.createObjectURL(result[rightKey] as Blob) : undefined
        })
      } else {
        const img = await unifiedCameraUtils.captureImage(
          'primary',
          applyFilter,
          cropRatio,
          useShutterPrimary,
          undefined,
          cropRects.primary
        )
        setTestShots({ left: img ? URL.createObjectURL(img) : undefined })
      }
    } catch (error) {
      console.error('[KOTAK] Test capture failed:', error)
    } finally {
      setTesting(false)
    }
  }

  // Photo progress counter (same shape as the regular bahela capture page). Recomputed each
  // render; remainingPhotosRef is decremented right before the gallery update that re-renders us.
  const captureProgress = {
    current:
      userPreferences.retakeCount > 0
        ? userPreferences.retakeCount - remainingPhotosRef.current
        : totalPhotos - remainingPhotosRef.current,
    total: userPreferences.retakeCount > 0 ? userPreferences.retakeCount : totalPhotos
  }

  // ── Layout helpers ───────────────────────────────────────────────────────────────────────
  const paneFrameClass =
    'relative shrink-0 overflow-hidden rounded-[28px] border-[6px] border-[#3d2b1f] bg-black shadow-[inset_0_0_40px_rgba(0,0,0,0.8)]'

  // Locked panes take the crop's final aspect (cropRatio); sized so two fit side by side.
  const lockedPaneStyle: React.CSSProperties = {
    aspectRatio: String(cropRatio),
    height: dualActive
      ? `min(88vh, calc((50vw - 6rem) / ${cropRatio}))`
      : `min(88vh, calc(85vw / ${cropRatio}))`
  }

  const renderEditorPane = (key: CameraKey): React.ReactNode => {
    const dims = sensorDims[key]
    const dAspect = dims ? displayAspect(dims.w, dims.h, rot[key]) : 4 / 3
    const editStyle: React.CSSProperties = {
      aspectRatio: String(dAspect),
      height: dualActive ? `min(74vh, calc((50vw - 5rem) / ${dAspect}))` : `min(74vh, calc(80vw / ${dAspect}))`
    }
    const sensorRect = effectiveSensorRect(key)
    const displayRect = dims && sensorRect ? sensorRectToDisplayRect(sensorRect, rot[key], mir[key]) : null
    const boxAspect = cropRatio / dAspect

    return (
      <div className={paneFrameClass} style={editStyle}>
        <Preview
          key={`kotak-edit-${key}-${selectedFilter?._id}`}
          adjustments={selectedFilter?.adjustments}
          filter={selectedFilter?._id}
          postLUTAdjustments={selectedFilter?.postLUTAdjustments}
          cameraKey={key}
          forceFullSensor
        />
        {displayRect ? (
          <CropEditor
            rect={displayRect}
            boxAspect={boxAspect}
            onChange={(r) => onEditorChange(key, r)}
            label={key === leftKey ? (i18n.language === 'id' ? 'Kiri' : 'Left') : i18n.language === 'id' ? 'Kanan' : 'Right'}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 text-lg">
            {i18n.language === 'id' ? 'Menyiapkan kamera…' : 'Preparing camera…'}
          </div>
        )}
      </div>
    )
  }

  // ── Admin (unlocked) view ────────────────────────────────────────────────────────────────
  if (editing) {
    const ratioLabel = (() => {
      const preset = RATIO_PRESETS.find((p) => Math.abs(p.value - cropRatio) < 0.001)
      return preset ? preset.label : cropRatio.toFixed(3)
    })()
    return (
      <div className="relative w-full h-full bg-carbon overflow-auto">
        <div id="flash" className="w-full h-full flash absolute inset-0 pointer-events-none"></div>

        {/* Toolbar */}
        <div className="w-full px-6 pt-4 pb-2 flex flex-wrap items-center gap-3 text-white">
          <span className="font-serif-bahela text-2xl">
            {i18n.language === 'id' ? 'Mode Admin — Atur Kotak' : 'Admin — Adjust Boxes'}
          </span>
          <span className="text-white/60 text-sm">
            {i18n.language === 'id'
              ? 'Seret & ubah ukuran kotak agar pas dengan jendela fisik. Kunci layar (fullscreen) untuk mode tamu.'
              : 'Drag & resize each box to match the physical window. Lock the screen (fullscreen) for guest mode.'}
          </span>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-white/70">{i18n.language === 'id' ? 'Rasio' : 'Ratio'}:</span>
            {RATIO_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => applyRatio(p.value)}
                className={`px-3 py-1 rounded text-sm border ${
                  Math.abs(p.value - cropRatio) < 0.001
                    ? 'bg-white text-[#3d2b1f] border-white font-semibold'
                    : 'bg-transparent text-white border-white/40'
                }`}
              >
                {p.label}
              </button>
            ))}
            <span className="text-xs text-white/50 ml-1">({ratioLabel})</span>
          </div>
        </div>

        {/* Panes — rendered in the operator's chosen L/R order, with a center swap control so the
            operator can flip which physical camera is the left vs right box without guessing the
            dropdown order. */}
        <div className="w-full flex items-center justify-center gap-6 px-6 py-4">
          {renderEditorPane(leftKey)}
          {dualActive && (
            <button
              onClick={() => toggleSwapped(projectKey)}
              title={i18n.language === 'id' ? 'Tukar Kiri/Kanan' : 'Switch Left/Right'}
              className="shrink-0 flex flex-col items-center gap-1 px-4 py-3 rounded-2xl border border-white/40 bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <FaExchangeAlt className="text-3xl" />
              <span className="text-sm whitespace-nowrap">
                {i18n.language === 'id' ? 'Tukar Kiri/Kanan' : 'Switch L/R'}
              </span>
            </button>
          )}
          {dualActive && renderEditorPane(rightKey)}
        </div>

        {/* Actions */}
        <div className="w-full px-6 pb-6 flex items-center justify-center gap-4">
          <Button onClick={runTestCapture} className="text-2xl" textColor="methadone">
            {testing
              ? i18n.language === 'id'
                ? 'Mengambil…'
                : 'Capturing…'
              : i18n.language === 'id'
                ? 'Tes Foto'
                : 'Test Capture'}
          </Button>
          <button
            onClick={() => resetProject(projectKey)}
            className="px-4 py-2 rounded border border-white/40 text-white/80 text-lg"
          >
            {i18n.language === 'id' ? 'Reset Kotak' : 'Reset Boxes'}
          </button>
          {(testShots.left || testShots.right) && (
            <div className="flex items-center gap-3 ml-4">
              {testShots.left && (
                <img src={testShots.left} alt="test-left" className="h-24 rounded-md border border-white/30" crossOrigin="anonymous" />
              )}
              {testShots.right && (
                <img src={testShots.right} alt="test-right" className="h-24 rounded-md border border-white/30" crossOrigin="anonymous" />
              )}
            </div>
          )}
        </div>

        {dualSuppressedByFlag && (
          <div className="px-6 pb-4 text-[12px] text-white/40">
            Dual kamera nonaktif: fitur &quot;dual_camera&quot; belum diaktifkan untuk akun ini
          </div>
        )}
      </div>
    )
  }

  // ── Locked (end-user) view ───────────────────────────────────────────────────────────────
  return (
    <div className="relative w-full h-full bg-carbon">
      <div id="flash" className="w-full h-full flash"></div>

      {dualSuppressedByFlag && (
        <div className="absolute bottom-2 left-2 z-[9] text-[11px] text-white/40">
          Dual kamera nonaktif: fitur &quot;dual_camera&quot; belum diaktifkan untuk akun ini
        </div>
      )}

      {isCaptureError && (
        <div className="absolute inset-0 bg-black/80 z-[7] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-red-400 mb-4 font-serif-bahela">
              {t('capture.captureFailed', 'Capture Failed')}
            </h2>
            <p className="text-xl text-white/80">
              {t('capture.retryingMoment', 'Retrying in a moment...')}
            </p>
          </div>
        </div>
      )}

      {/* "Stay still" feedback while the physical shutter round transfers over USB. */}
      {isCapturingWithShutter && (
        <div className="absolute inset-0 bg-white/50 z-[6] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-black mb-4 font-serif-bahela">
              📸 {t('capture.lookAtCamera', 'Look at the Camera')}
            </h2>
            <p className="text-xl text-black/80">
              {t('capture.stayStill', 'Please stay still while we capture your photo...')}
            </p>
          </div>
        </div>
      )}

      {/* Left box | center (last captured pair) | right box */}
      <div className="absolute inset-0 flex items-center justify-center gap-4 px-6 py-6">
        <div className={paneFrameClass} style={lockedPaneStyle}>
          <Preview
            key={`kotak-left-${leftKey}-${selectedFilter?._id}`}
            adjustments={selectedFilter?.adjustments}
            filter={selectedFilter?._id}
            postLUTAdjustments={selectedFilter?.postLUTAdjustments}
            cameraKey={leftKey}
            ratio={cropRatio}
            cropRect={savedRects[leftKey]}
            fillContainer
            onRecordingCanvasReady={shouldRecord ? onRecCanvasReadyFor(leftKey) : undefined}
          />
        </div>

        <div className="w-28 shrink-0 flex flex-col items-center justify-center gap-4">
          {lastCaptured.left && (
            <img
              src={lastCaptured.left}
              alt="left"
              className="w-full rounded-md object-cover"
              style={{ aspectRatio: String(cropRatio) }}
              crossOrigin="anonymous"
            />
          )}
          {lastCaptured.right && (
            <img
              src={lastCaptured.right}
              alt="right"
              className="w-full rounded-md object-cover"
              style={{ aspectRatio: String(cropRatio) }}
              crossOrigin="anonymous"
            />
          )}
        </div>

        {dualActive && (
          <div className={paneFrameClass} style={lockedPaneStyle}>
            <Preview
              key={`kotak-right-${rightKey}-${selectedFilter?._id}`}
              adjustments={selectedFilter?.adjustments}
              filter={selectedFilter?._id}
              postLUTAdjustments={selectedFilter?.postLUTAdjustments}
              cameraKey={rightKey}
              ratio={cropRatio}
              cropRect={savedRects[rightKey]}
              fillContainer
              onRecordingCanvasReady={shouldRecord ? onRecCanvasReadyFor(rightKey) : undefined}
            />
          </div>
        )}
      </div>

      {/* Shared countdown overlay */}
      {capturing && !preparing && (
        <div className="absolute inset-0 z-[5] flex items-center justify-center pointer-events-none">
          <span
            key={captureCountdown}
            className="text-[280px] text-white animate-countdown drop-shadow-lg font-serif-bahela"
          >
            {captureCountdown}
          </span>
        </div>
      )}

      {/* Photo counter (same as the regular bahela capture page) */}
      {!preparing && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-[6] pointer-events-none">
          <span className="text-white text-6xl font-serif-bahela drop-shadow-lg">
            {captureProgress.current}/{captureProgress.total}
          </span>
        </div>
      )}

      {/* Prep overlay */}
      {preparing && (
        <div className="w-full h-full absolute top-0 left-0 z-[8] bg-black/60 flex flex-col text-white justify-center gap-8 items-center">
          <h1 className="text-[80px] drop-shadow-2xl text-center flex flex-col gap-2 text-white font-serif-bahela leading-none">
            <span>{i18n.language === 'id' ? 'Waktunya' : 'Prepare'}</span>
            <span>{i18n.language === 'id' ? 'bersiap,' : 'Yourselves!'}</span>
            {i18n.language === 'id' && <span>Tuan Nona!</span>}
          </h1>
          <p className="font-body-bahela text-3xl text-white/80 max-w-3xl text-center px-8">
            {dualActive
              ? i18n.language === 'id'
                ? 'Masing-masing masuk ke kotak kalian — dua kamera memotret bersamaan!'
                : 'Step into your own box — both cameras shoot together!'
              : i18n.language === 'id'
                ? 'Bersiap di depan kamera!'
                : 'Get ready in front of the camera!'}
          </p>
          <span className="font-body-bahela text-[50px] drop-shadow-2xl">{prepCountdown}</span>
          <Button onClick={startCaptureSession} className="text-[44px]" textColor="methadone">
            {i18n.language === 'id' ? 'Mulai Foto' : 'Start Capturing'}
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Dispatcher: a solo guest who ticked a single box on the camera-choice page runs the REGULAR
 * single-camera bahela capture (using their chosen `selectedCamera`); everyone else runs the
 * dual-camera kotak capture. The flag is set once per session before this page mounts, so the
 * branch is stable for the component's lifetime (no conditional-hook hazard).
 */
const CapturePage: React.FC = () => {
  const kotakSingleCamera = useSessionStateStore((s) => s.userPreferences.kotakSingleCamera)
  return kotakSingleCamera ? <BahelaCapturePage /> : <KotakDualCapture />
}

export default CapturePage
